import React from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title, DoughnutController } from "chart.js";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
import { InsurerExposure } from "api/api-risk-dashboard.d";

ChartJS.register(ArcElement, Tooltip, Legend, Title, DoughnutController);

interface InsurerExposurePieChartProps {
  insurerData?: InsurerExposure;
  isLoading?: boolean;
  error?: any;
  showTitle?: boolean;
}

const InsurerExposurePieChart: React.FC<InsurerExposurePieChartProps> = ({ 
  insurerData,
  isLoading = false,
  error = null,
  showTitle = false
}) => {
  const theme = useTheme();
  
  // Define vibrant colors for insurers
  const colors = [
    "#8E24AA", // Purple
    "#D84315", // Deep Orange
    "#0097A7", // Cyan
    "#5D4037", // Brown
    "#388E3C", // Green
    "#1565C0", // Blue
    "#6D4C41", // Brown
  ];
  
  // Transform API data for pie chart - moved to render time to prevent errors during initial render
  const createChartData = () => {
    // Early return with mock data if we're still loading
    if (isLoading) {
      return null;
    }
    
    // Don't log errors during initial loading
    if (!isLoading && !insurerData) {
      console.log("insurerData is undefined or null");
      return null;
    }
    
    // Check if data exists and has the expected structure
    if (!insurerData?.data) {
      return null;
    }
    
    if (!Array.isArray(insurerData.data)) {
      console.log("insurerData.data is not an array", insurerData.data);
      return null;
    }
    
    if (insurerData.data.length === 0) {
      return null;
    }
    
    const insurers = insurerData.data;
    
    return {
      labels: insurers.map((item) => `${item.insurer_name} (${item.count})`),
      datasets: [
        {
          data: insurers.map((item) => item.count),
          backgroundColor: insurers.map((_, index) => colors[index % colors.length]),
          borderColor: "#FFFFFF",
          borderWidth: 2,
          hoverOffset: 15,
          hoverBackgroundColor: insurers.map((_, index) => {
            const color = colors[index % colors.length];
            return `${color}CC`; // Add transparency for hover effect
          }),
        },
      ],
    };
  };

  // Only create chart data when rendering to prevent errors
  const chartData = createChartData();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Typography color="error">Failed to load insurer data</Typography>
      </Box>
    );
  }

  if (!chartData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px", flexDirection: "column", gap: 2 }}>
        <Typography color="text.secondary">No insurer data available</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
          Data may still be loading or the API response format is unexpected
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      {showTitle && (
        <Typography variant="h6" align="center" sx={{ mb: 2 }}>Exposure - Insurer</Typography>
      )}
      <Box sx={{ height: showTitle ? "calc(100% - 50px)" : "100%" }}>
        <Pie 
          data={chartData} 
          options={{ 
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  usePointStyle: true,
                  padding: 15,
                  color: theme.palette.text.primary,
                  font: {
                    weight: 'bold'
                  },
                  boxWidth: 10
                }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                bodyFont: {
                  size: 14
                },
                callbacks: {
                  label: function (context) {
                    const label = context.label || '';
                    const value = context.raw || 0;
                    const dataset = context.dataset;
                    const total = dataset.data.reduce((acc: number, data: number) => acc + data, 0);
                    const percentage = Math.round((value as number / total) * 100);
                    return `${label}: ${percentage}% (Count: ${value})`;
                  },
                  title: function (tooltipItems) {
                    return tooltipItems[0].label.split(' (')[0]; // Only show insurer name in title
                  }
                }
              }
            },
            animation: {
              animateScale: true,
              animateRotate: true
            }
          }} 
        />
      </Box>
    </Box>
  );
};

export default InsurerExposurePieChart;
